import React, { useEffect, useState } from "react";
import { api } from "../api";

function fmtRec(v?: number | null): string {
  if (v == null || isNaN(v)) return "?";
  if (v >= 1e6) return (v / 1e6).toFixed(1) + "M";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + "K";
  return Math.round(v).toLocaleString();
}

function fmtDur(mins?: number | null): string {
  if (mins == null) return "\u2014";
  const m = Math.round(mins);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
}

function pctDiff(actual: number, base?: number | null): number | null {
  if (!base) return null;
  return Math.round(((actual - base) / base) * 100);
}

interface VolumePredictionProps {
  asset: string | null;
}

interface CatchupSectionProps {
  item: any;
  backlog: number;
  mult: number;
  hasBaseline: boolean;
  missedSlots: any[];
}

const CatchupSection: React.FC<CatchupSectionProps> = ({ item, backlog, mult, hasBaseline, missedSlots }) => (
  <div style={{ background: "#1e1408", border: "1px solid #7a5c00", borderRadius: 6, padding: "10px 14px", marginBottom: 10 }}>
    <div style={{ color: "#ffc107", fontWeight: 600, fontSize: ".82rem", marginBottom: 6 }}>
      &#9888; CATCH-UP PREDICTION &nbsp;
      <span style={{ fontWeight: 400, color: "#aaa", fontSize: ".75rem" }}>
        {backlog} missed slot{backlog > 1 ? "s" : ""} &#8594; next run carries {mult}&#215; load
      </span>
    </div>
    <div style={{ marginBottom: 6, fontSize: ".76rem", color: "#bbb" }}>
      <strong style={{ color: "#ddd" }}>Backlog:</strong>&nbsp;
      {missedSlots.length
        ? missedSlots.map((s: any, i: number) => (
            <span key={i} style={{ display: "inline-block", background: "#3a1a1a", color: "#ff9", borderRadius: 3, padding: "1px 6px", fontSize: ".72rem", margin: "1px 2px" }}>
              {s.time}
            </span>
          ))
        : <em>&#8212;</em>}
    </div>
    <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginTop: 6 }}>
      <div className="metric-item">
        <div className="metric-value" style={{ color: "#ffc107", fontSize: "1.2rem" }}>
          {hasBaseline && item.vol_avg_out != null ? fmtRec(item.vol_avg_out * mult) : "\u2014"}
        </div>
        <div className="metric-label">Est. Records (next run)</div>
      </div>
      <div className="metric-item">
        <div className="metric-value" style={{ color: "#ffc107", fontSize: "1.2rem" }}>
          {hasBaseline && item.vol_avg_dur != null
            ? `${fmtDur(item.vol_avg_dur * (1 + 0.6 * (mult - 1)))} \u2013 ${fmtDur(item.vol_avg_dur * (1 + 1.0 * (mult - 1)))}`
            : "\u2014"}
        </div>
        <div className="metric-label">Est. Duration (next run)</div>
      </div>
      <div className="metric-item">
        <div className="metric-value" style={{ color: "#aaa", fontSize: "1.2rem" }}>{mult}&#215;</div>
        <div className="metric-label">Catch-up Multiplier</div>
      </div>
    </div>
    <div style={{ fontSize: ".68rem", color: "#666", marginTop: 6, borderTop: "1px solid #333", paddingTop: 5 }}>
      Assumes delta-load pipeline &#183; Duration range uses 0.6&#215; \u2013 1.0&#215; scaling factor
    </div>
  </div>
);

interface TodayRunWithBaselineProps {
  item: any;
  p: number | null;
  durLabel: string;
  barPct: number;
  barColor: string;
}

const TodayRunWithBaseline: React.FC<TodayRunWithBaselineProps> = ({ item, p, durLabel, barPct, barColor }) => (
  <div style={{ background: "#0d1e0d", border: "1px solid #1e5c1e", borderRadius: 6, padding: "10px 14px", marginBottom: 10 }}>
    <div style={{ color: "#5cb85c", fontWeight: 600, fontSize: ".82rem", marginBottom: 6 }}>
      &#10003; TODAY'S RUN &nbsp;
      <span style={{ fontWeight: 400, color: "#aaa", fontSize: ".75rem" }}>{durLabel}</span>
    </div>
    <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-end" }}>
      <div className="metric-item">
        <div className="metric-value" style={{ color: "#5cb85c", fontSize: "1.2rem" }}>
          {fmtDur(item.actual_duration_mins)}
          {p != null && (
            <span style={{ fontSize: ".75rem", color: Math.abs(p) > 15 ? (p > 0 ? "#e05" : "#2a9") : "#888", marginLeft: 6 }}>
              {p > 0 ? "+" : "\u2212"}{Math.abs(p)}% vs baseline
            </span>
          )}
        </div>
        <div className="metric-label">Actual Duration</div>
      </div>
      <div className="metric-item" style={{ flex: 1, minWidth: 140 }}>
        <div style={{ fontSize: ".72rem", color: "#888", marginBottom: 3 }}>vs baseline {fmtDur(item.vol_avg_dur)}</div>
        <div style={{ background: "#333", borderRadius: 3, height: 8, overflow: "hidden" }}>
          <div style={{ background: barColor, width: `${Math.min(barPct, 100)}%`, height: "100%", borderRadius: 3, transition: "width .4s" }}></div>
        </div>
        <div style={{ fontSize: ".68rem", color: "#555", marginTop: 2 }}>{barPct}% of avg duration</div>
      </div>
    </div>
  </div>
);

const TodayRunNoBaseline: React.FC<{ item: any }> = ({ item }) => (
  <div style={{ background: "#0d1e0d", border: "1px solid #1e5c1e", borderRadius: 6, padding: "10px 14px", marginBottom: 10 }}>
    <div style={{ color: "#5cb85c", fontWeight: 600, fontSize: ".82rem", marginBottom: 4 }}>&#10003; TODAY'S RUN</div>
    <div className="metric-item">
      <div className="metric-value" style={{ color: "#5cb85c" }}>{fmtDur(item.actual_duration_mins)}</div>
      <div className="metric-label">Actual Duration</div>
    </div>
  </div>
);

const BaselineSection: React.FC<{ item: any }> = ({ item }) => (
  <div style={{ borderTop: "1px solid #333", paddingTop: 8, marginTop: 2 }}>
    <div style={{ fontSize: ".72rem", color: "#666", marginBottom: 5, textTransform: "uppercase", letterSpacing: ".04em" }}>
      Per-Slot Baseline &nbsp;&#183;&nbsp; 14d &#183; same day-of-week &#183; same 15-min slot
    </div>
    <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
      {item.vol_avg_in != null && (
        <div className="metric-item">
          <div className="metric-value" style={{ color: "#7d8590" }}>{fmtRec(item.vol_avg_in)}</div>
          <div className="metric-label">Avg Records In</div>
        </div>
      )}
      <div className="metric-item">
        <div className="metric-value" style={{ color: "#53a8b6" }}>{fmtRec(item.vol_avg_out)}</div>
        <div className="metric-label">Avg Records Out</div>
      </div>
      <div className="metric-item">
        <div className="metric-value" style={{ color: "#53a8b6" }}>{fmtDur(item.vol_avg_dur)}</div>
        <div className="metric-label">Avg Duration</div>
      </div>
      <div className="metric-item">
        <div className="metric-value" style={{ color: "#7d8590" }}>{item.vol_run_count || "?"}</div>
        <div className="metric-label">Baseline Runs</div>
      </div>
    </div>
  </div>
);

function computeDurLabel(p: number | null): string {
  if (p == null) return "";
  if (p > 15) return "Slower than usual";
  if (p < -15) return "Faster than usual";
  return "Within normal range";
}

// Mirrors app.js renderVolumePrediction(), which reads the same "item" fields
// (vol_avg_*, actual_duration_mins, catchup_multiplier, missed slots) off the
// live dashboard item — fetched here via api.status() since only the asset
// name is available to this panel.
const VolumePrediction: React.FC<VolumePredictionProps> = ({ asset }) => {
  const [item, setItem] = useState<any>(null);

  useEffect(() => {
    if (!asset) return;
    setItem(null);
    api.status().then(({ ok, data }) => {
      if (!ok || !data) return;
      const found = (data.items || []).find((i: any) => i.data_asset === asset);
      setItem(found || null);
    });
  }, [asset]);

  if (!item) return null;

  const hasBaseline = item.vol_avg_out != null;
  const hasActual = item.actual_duration_mins != null;
  const backlog = item.total_backlog || 0;
  const mult = item.catchup_multiplier || 1;
  const isMissed = item.status === "missed";
  const isScheduled = item.status === "scheduled";

  if (!hasBaseline && !hasActual && backlog === 0) return null;

  const p = hasActual && hasBaseline ? pctDiff(item.actual_duration_mins, item.vol_avg_dur) : null;
  const barPct = hasActual && item.vol_avg_dur
    ? Math.min(200, Math.round((item.actual_duration_mins / item.vol_avg_dur) * 100))
    : 100;
  const barColor = p != null && Math.abs(p) > 15 ? (p > 0 ? "#e05050" : "#2aaa77") : "#53a8b6";
  const durLabel = computeDurLabel(p);

  const missedSlots: any[] = [...(item.missed_today_slots || []), ...(item.missed_yest_slots || [])];
  const showCatchup = backlog > 0 && (isMissed || isScheduled || hasBaseline);
  const showNoData = !showCatchup && !hasActual && !hasBaseline;

  return (
    <div className="detail-section" id="detail-volume-section">
      <h3>Run Prediction &amp; Baseline</h3>
      <div id="detail-volume">
        {showCatchup && (
          <CatchupSection item={item} backlog={backlog} mult={mult} hasBaseline={hasBaseline} missedSlots={missedSlots} />
        )}

        {hasActual && hasBaseline && (
          <TodayRunWithBaseline item={item} p={p} durLabel={durLabel} barPct={barPct} barColor={barColor} />
        )}

        {hasActual && !hasBaseline && <TodayRunNoBaseline item={item} />}

        {hasBaseline && <BaselineSection item={item} />}

        {showNoData && <div style={{ color: "#666", fontSize: ".8rem" }}>No prediction data available</div>}
      </div>
    </div>
  );
};

export default VolumePrediction;
